// Minimal client-side routing (history API). The server serves index.html for any path.
import { useEffect, useState } from 'react';

export type Route =
  | { name: 'prs' }
  | { name: 'pr'; number: number; file?: string; anchor?: string }
  | { name: 'files' };

export function parseRoute(path: string, search = '', hash = ''): Route {
  const m = /^\/pr\/(\d+)\/?$/.exec(path);
  if (m) {
    const r: Route = { name: 'pr', number: Number(m[1]) };
    const file = new URLSearchParams(search).get('file');
    if (file) r.file = file;
    if (hash.length > 1) r.anchor = decodeURIComponent(hash.slice(1));
    return r;
  }
  if (path === '/files') return { name: 'files' };
  return { name: 'prs' };
}

export function routePath(r: Route): string {
  if (r.name === 'pr') return `/pr/${r.number}${r.file ? `?file=${encodeURIComponent(r.file)}` : ''}${r.anchor ? `#${encodeURIComponent(r.anchor)}` : ''}`;
  if (r.name === 'files') return '/files';
  return '/';
}

const EVENT = 'hq:navigate';

export function navigate(r: Route, { replace = false } = {}) {
  const path = routePath(r);
  if (path === location.pathname + location.search + location.hash) return;
  if (replace) history.replaceState(null, '', path); else history.pushState(null, '', path);
  dispatchEvent(new Event(EVENT));
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(location.pathname, location.search, location.hash));
  useEffect(() => {
    const update = () => setRoute(parseRoute(location.pathname, location.search, location.hash));
    addEventListener('popstate', update);
    addEventListener(EVENT, update);
    return () => { removeEventListener('popstate', update); removeEventListener(EVENT, update); };
  }, []);
  return route;
}

/** An <a> that navigates in-app (keeps normal link semantics: middle-click, copy link). */
export function linkProps(r: Route) {
  return {
    href: routePath(r),
    onClick: (e: { preventDefault(): void; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; button: number }) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      navigate(r);
    },
  };
}
